import { DataAction, Payload } from '@angular-ru/ngxs/decorators';
import { EventEmitter, Injector, Optional, Type } from '@angular/core';
import { ActivatedRoute, Event, NavigationEnd, Router } from '@angular/router';
import { createSelector, Store } from '@ngxs/store';
import { produce } from 'immer';
import { ConfirmationService } from 'primeng/api';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, shareReplay, switchMap } from 'rxjs/operators';
import {
    IProductCategoryContext,
    IProductContextAssessmentStatus,
    IProductContextCase,
    IProductContextCaseStatus,
    IProductContextMetadataOwner,
    IProductStage,
    MatchingCaseFunc
} from 'src/app/models/case';
import { MMRRecord } from 'src/app/models/member';
import { Product } from 'src/app/models/product';
import { SignalREvent } from 'src/app/models/signalr';
import { InterviewHandlerService } from 'src/app/shared/interview-new/services/interview-handler.service';
import { FeatureService } from 'src/app/shared/preview-feature/preview-feature.service';

import { AlertService } from '../alert.service';
import { CaseService } from '../case/case.service';
import { MemberService } from '../member/member.service';
import { ProductContextService } from '../product-context/product-context.service';
import { UserService } from '../user/user.service';
import { BaseProductService, ProductSelectorExtentions } from './-base-product.service';
import { ProductCategoryNames } from './../../models/case';
import { INotification } from './../../models/notifications';
import { IInterviewHandler, IInterviewSettings, InterviewType, IVariableEvaluationModel } from './../../shared/interview-new/models/interview-handler.models';
import { BaseProductStateModel, MultipleBaseProductStateModel } from './base-product-state.models';
import { BaseProductInterviewData } from './_base-product-interview-data';
import { TaskOrchestratorService } from 'src/app/services/task-orchestrator/task-orchestrator.service';

export abstract class BaseProductServiceMultiple<T extends MultipleBaseProductStateModel> extends BaseProductService<T> {

    abstract get stateToken();

    abstract matchingCaseFunc?: MatchingCaseFunc;

    notInterestedActionId = 'NotInterested';
    notQualifiedActionId = 'NotQualified';

    evaluate$ = new EventEmitter<any>();
    smartActionContext$ = this._smartActionContext$.asObservable().pipe(shareReplay(1));

    loadCustomView$ = new BehaviorSubject<string>(null);

    currentCustomViewMetadata: any;

    signalR$ = this.memberService.signalR$.pipe(filter((e: SignalREvent) => this.signalRFilter(e)));
    routeEnd$$ = this.router.events.subscribe((event: Event) => {

        if (event instanceof NavigationEnd) {
            this.updateAllowedActions$.next(null);
        }
    });

    forceLoadProductCase$ = new EventEmitter<boolean>(true);

    public memberService: MemberService;
    interviewData: BaseProductInterviewData;
    urlSubscription: any;



    updateAllowedActions$ = new EventEmitter<any>();

    mmr$: Observable<MMRRecord>;
    interviewHandler: InterviewHandlerService;

    get interviewType() {
        switch (this.productCategory) {
            case 'Community':
                return InterviewType.Community;
            case 'Medicaid':
                return InterviewType.Medicaid;
            case 'SNAP':
                return InterviewType.SNAP;
            case 'LIS':
                return InterviewType.LIS;
            case 'VA':
                return InterviewType.Veteran;
        }
    }





    static currentCase<T extends BaseProductStateModel>(instance: BaseProductService<T>) {
        return createSelector([instance.stateToken], (state: MultipleBaseProductStateModel) => {
            const currentCaseId = state.currentCaseId;
            return BaseProductServiceMultiple.getCase(state.productCategoryContext, currentCaseId);
        });
    }

    static getCase(context: IProductCategoryContext, currentCaseId?: string) {
        return context.cases.find(_ => _.id === currentCaseId)

    }

    get cases$(): Observable<IProductContextCase[]> {
        return this.store.select<IProductContextCase[]>(ProductSelectorExtentions.cases(this));
    }

    get casesSync(): IProductContextCase[] {
        return this.store.selectSnapshot<IProductContextCase[]>(ProductSelectorExtentions.cases(this));
    }

    // abstract get iconSync(): string;
    // abstract get icon$(): Observable<string>;

    // abstract get statusSync(): IProductContextCaseStatus;
    // abstract get status$(): Observable<IProductContextCaseStatus>;


    // abstract get caseOwnerSync(): IProductContextMetadataOwner;
    // abstract get caseOwner$(): Observable<IProductContextMetadataOwner>;


    // get sourceProductSync(): Product {
    //     return this.snapshot.sourceProduct;
    // }

    // abstract get sourceProduct$(): Observable<Product>;

    // abstract get productCategoryContextSync(): IProductCategoryContext;

    // abstract get productCategoryContext$(): Observable<IProductCategoryContext>;

    // abstract get categoryDisplayNameSync(): string;
    // abstract get categoryDisplayName$(): Observable<string>;

    // abstract get casesSync(): IProductContextCase[];
    // abstract get cases$(): Observable<IProductContextCase[]>;

    // abstract get currentCaseSync(): IProductContextCase;
    // abstract get currentCase$(): Observable<IProductContextCase>;


    // abstract get caseStageSync(): IProductStage;
    // abstract get caseStage$(): Observable<IProductStage>;

    // get notificationsSync(): INotification[] {
    //     return this.snapshot.notifications;
    // }

    // abstract get notifications$(): Observable<INotification[]>;

    // abstract get caseAssessmentSync(): IProductContextAssessmentStatus;
    // abstract get caseAssessment$(): Observable<IProductContextAssessmentStatus>;

    // abstract get noCaseMessageSync(): string;
    // abstract get noCaseMessage$(): Observable<string>;

    // abstract get stagesSync(): IProductStage[];
    // abstract get stages$(): Observable<IProductStage[]>;


    abstract signalRFilter(e: SignalREvent): boolean;

    constructor(
        @Optional() public productCategory: keyof typeof ProductCategoryNames,
        memberService: MemberService,
        public userService: UserService,
        protected caseService: CaseService,
        protected productContextService: ProductContextService,
        public confirmationService: ConfirmationService,
        public router: Router,
        public route: ActivatedRoute,
        protected alertService: AlertService,
        interviewDataType: Type<BaseProductInterviewData>,
        protected store: Store,
        featureService: FeatureService,
        interviewHandler: IInterviewHandler,
        taskOrchestratorService: TaskOrchestratorService
    ) {
        super(
            productCategory,
            memberService,
            userService,
            caseService,
            productContextService,
            confirmationService,
            router,
            route,
            alertService,
            interviewDataType,
            store,
            featureService,
            interviewHandler,
            taskOrchestratorService
        );

    }

    async ngxsAfterBootstrap() {
        super.ngxsAfterBootstrap();

    }


    @DataAction()
    setCurrentCase(@Payload('case') $case: IProductContextCase) {
        this.ctx.setState(produce(this.ctx.getState(), (draft: T) => {
            draft.currentCaseId = $case ? $case.id : undefined;
        }));
    }

    addCase(context?: {}): Observable<IProductContextCase> {
        return this.caseService.addCase(this.memberService.base64Sync, this.productCategory, context).pipe(switchMap(_ => {
            return this.refreshCase();
        }));
    }

    async evaluateSecondaryInterview(settings: IInterviewSettings, variables: IVariableEvaluationModel[]) {

    }

    async initializeSecondaryInterview(externalInterviewSettings: IInterviewSettings, injector: Injector) {

    }

}
